import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpCircleIcon, CheckIcon, SparklesIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useSubscription } from '../../hooks/useSubscription';
import { SUBSCRIPTION_TIERS } from '../../stores/subscriptionStore';

const TIER_FEATURES = {
  [SUBSCRIPTION_TIERS.GROWTH]: [
    'Shopify Integration',
    'AI-powered product tagging',
    'Auto layout & DPI scoring',
    'CraftFlow Commerce storefront',
    'Custom domain support',
    'Up to 300 exports/month',
  ],
  [SUBSCRIPTION_TIERS.PRO]: [
    'All Growth features',
    'Advanced layout automation',
    'Advanced analytics & reporting',
    'Multi-shop support (up to 3)',
    'Priority queue & scheduled runs',
    'Unlimited exports',
    'Priority support',
  ],
  [SUBSCRIPTION_TIERS.ENTERPRISE]: [
    'All Pro features',
    'White-label branding',
    'Multi-user with role-based access',
    'Unlimited shops & seats',
    'Custom integrations',
    'Dedicated account manager',
  ],
};

const UpgradePrompt = ({
  feature,
  recommendedTier,
  message,
  className = '',
  size = 'default', // 'small', 'default', 'large'
  variant = 'card', // 'card', 'banner', 'modal'
}) => {
  const { getTierConfig, currentTier } = useSubscription();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const recommendedConfig = getTierConfig(recommendedTier);
  const currentConfig = getTierConfig(currentTier);

  const handleUpgrade = () => {
    navigate(`/subscription/checkout?plan=${recommendedTier}`);
  };

  const priceLabel = recommendedConfig?.price != null ? `$${recommendedConfig.price}/mo` : 'Custom';

  if (variant === 'banner') {
    return (
      <div
        className={`bg-gradient-to-r from-lavender-50 to-peach-50 border border-lavender-200 dark:border-lavender-800 rounded-xl p-4 ${className}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-lavender-100 rounded-full flex items-center justify-center">
                <ArrowUpCircleIcon className="w-5 h-5 text-lavender-600" />
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-sage-800 dark:text-sage-200">
                {message || `Upgrade to ${recommendedConfig?.name} to unlock this feature`}
              </p>
            </div>
          </div>
          <button
            onClick={handleUpgrade}
            className="bg-lavender-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-lavender-700 transition-colors"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    );
  }

  if (size === 'small') {
    return (
      <div
        className={`bg-gradient-to-b from-sage-50 to-lavender-50/50 border border-sage-200 rounded-xl p-4 text-center ${className}`}
      >
        <div className="w-8 h-8 bg-lavender-100 rounded-full flex items-center justify-center mx-auto mb-2">
          <LockClosedIcon className="w-4 h-4 text-lavender-600" />
        </div>
        <p className="text-xs text-sage-600 mb-3">{message || `Requires ${recommendedConfig?.name}`}</p>
        <button
          onClick={handleUpgrade}
          className="text-xs bg-lavender-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-lavender-700 transition-colors"
        >
          Upgrade - {priceLabel}
        </button>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-sage-200 rounded-2xl p-6 text-center shadow-xs ${className}`}>
      <div className="mb-4">
        <div className="w-14 h-14 bg-gradient-to-br from-lavender-100 dark:from-lavender-900/30 to-peach-100 dark:to-peach-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <SparklesIcon className="w-7 h-7 text-lavender-600" />
        </div>
        <h3 className="text-lg font-semibold text-sage-900 mb-2">Unlock This Feature</h3>
        <p className="text-sage-600 mb-4">{message || `Available on the ${recommendedConfig?.name} plan`}</p>
      </div>

      <div className="space-y-4">
        <div className="bg-sage-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-sage-700">Current Plan</span>
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-semibold bg-${currentConfig?.color}-100 text-${currentConfig?.color}-700`}
            >
              {currentConfig?.name}
              {currentConfig?.price != null && ` - $${currentConfig.price}/mo`}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-sage-700">Recommended</span>
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-semibold bg-${recommendedConfig?.color}-100 text-${recommendedConfig?.color}-700`}
            >
              {recommendedConfig?.name} - {priceLabel}
            </span>
          </div>
        </div>

        {isExpanded && TIER_FEATURES[recommendedTier] && (
          <div className="bg-lavender-50 rounded-xl p-4 text-left">
            <h4 className="font-medium text-lavender-900 mb-3">What you get with {recommendedConfig?.name}:</h4>
            <ul className="space-y-2 text-sm text-lavender-800">
              {TIER_FEATURES[recommendedTier].map((item, i) => (
                <li key={i} className="flex items-center">
                  <CheckIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-1 bg-sage-100 text-sage-700 px-4 py-2.5 rounded-xl font-medium hover:bg-sage-200 transition-colors"
          >
            {isExpanded ? 'Less Info' : 'Learn More'}
          </button>
          <button
            onClick={handleUpgrade}
            className="flex-1 bg-gradient-to-r from-lavender-500 to-lavender-600 text-white px-4 py-2.5 rounded-xl font-medium hover:from-lavender-600 hover:to-lavender-700 transition-all shadow-xs"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
